import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
	Box,
	Title,
	Text,
	Button,
	Table,
	Modal,
	TextInput,
	Group,
	Card,
	Progress,
	Stack,
	SimpleGrid,
	Pagination,
	Menu,
	ActionIcon,
	Badge,
	Loader,
} from '@mantine/core'
import { getOrgExperiences } from '../services/actions/orgActions'
import {
	createExperience,
	duplicateExperience,
} from '../services/actions/experienceActions'
import { Copy, DotsVertical, Settings } from 'tabler-icons-react'

const EXPERIENCE_FIELD_MAX_LENGTH = 55
const ACTIVE_PAGE_SIZE = 12
const INACTIVE_PAGE_SIZE = 25
const STATS_REFRESH_INTERVAL_MS = 15_000
const CARD_INTERACTIVE_TARGET_SELECTOR = [
	'a',
	'button',
	'input',
	'select',
	'textarea',
	'summary',
	'[role="button"]',
	'[role="menu"]',
	'[role="menuitem"]',
	'[contenteditable="true"]',
].join(', ')

const getDateKey = (value) => {
	if (!value) return null

	const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
	if (match) return `${match[1]}-${match[2]}-${match[3]}`

	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return null

	return [
		date.getFullYear(),
		String(date.getMonth() + 1).padStart(2, '0'),
		String(date.getDate()).padStart(2, '0'),
	].join('-')
}

const getTodayKey = () => {
	const today = new Date()
	return [
		today.getFullYear(),
		String(today.getMonth() + 1).padStart(2, '0'),
		String(today.getDate()).padStart(2, '0'),
	].join('-')
}

const formatDate = (value) => {
	const key = getDateKey(value)
	if (!key) return ''

	const [year, month, day] = key.split('-').map(Number)
	return new Intl.DateTimeFormat(undefined, {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	}).format(new Date(year, month - 1, day))
}

const formatExperienceDates = (experience) => {
	const start = formatDate(experience.startDate)
	const end = formatDate(experience.endDate)

	if (
		start &&
		end &&
		getDateKey(experience.startDate) !== getDateKey(experience.endDate)
	) {
		return `${start} – ${end}`
	}
	if (start) return start
	if (end) return `Through ${end}`
	return 'No dates specified'
}

const getExperienceTiming = (experience, todayKey = getTodayKey()) => {
	const startKey = getDateKey(experience.startDate)
	const endKey = getDateKey(experience.endDate) || startKey

	if (!startKey && !endKey) {
		return { label: 'Dates TBD', color: 'gray', key: 'tbd' }
	}
	if (startKey && todayKey < startKey) {
		return { label: 'Upcoming', color: 'blue', key: 'upcoming' }
	}
	if (endKey && todayKey > endKey) {
		return { label: 'Completed', color: 'gray', key: 'completed' }
	}

	return { label: 'Live', color: 'red', key: 'live' }
}

const sortExperiencesByDate = (first, second) => {
	const firstDate = getDateKey(first.startDate) || getDateKey(first.endDate)
	const secondDate = getDateKey(second.startDate) || getDateKey(second.endDate)

	if (!firstDate && !secondDate) {
		return String(first.name || '').localeCompare(String(second.name || ''))
	}
	if (!firstDate) return 1
	if (!secondDate) return -1

	return (
		firstDate.localeCompare(secondDate) ||
		String(first.name || '').localeCompare(String(second.name || ''))
	)
}

const sortExperiencesByDateDescending = (first, second) => {
	const firstDate = getDateKey(first.startDate) || getDateKey(first.endDate)
	const secondDate = getDateKey(second.startDate) || getDateKey(second.endDate)

	if (!firstDate && !secondDate) {
		return String(first.name || '').localeCompare(String(second.name || ''))
	}
	if (!firstDate) return 1
	if (!secondDate) return -1

	return (
		secondDate.localeCompare(firstDate) ||
		String(first.name || '').localeCompare(String(second.name || ''))
	)
}

const ManageExperiencesPage = () => {
	const { orgSlug } = useParams()
	const navigate = useNavigate()

	const [experiences, setExperiences] = useState([])
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState('')
	const [showModal, setShowModal] = useState(false)
	const [experienceToDuplicate, setExperienceToDuplicate] = useState(null)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [searchTerm, setSearchTerm] = useState('')
	const [activePage, setActivePage] = useState(1)
	const [inactivePage, setInactivePage] = useState(1)
	const [newExperience, setNewExperience] = useState({
		name: '',
		slug: '',
	})

	const generateSlug = (value) =>
		value
			.toLowerCase()
			.replace(/[^a-z0-9\s-]/g, '')
			.trim()
			.replace(/\s+/g, '-')
			.replace(/^-+/, '')

	const closeExperienceModal = () => {
		if (isSubmitting) return
		setShowModal(false)
		setExperienceToDuplicate(null)
		setNewExperience({ name: '', slug: '' })
	}

	const openCreateModal = () => {
		setError('')
		setExperienceToDuplicate(null)
		setNewExperience({ name: '', slug: '' })
		setShowModal(true)
	}

	const openDuplicateModal = (experience) => {
		const copyName = `${String(experience.name || '').slice(
			0,
			EXPERIENCE_FIELD_MAX_LENGTH - 5,
		)} Copy`
		const slugBase = generateSlug(experience.slug || experience.name).slice(
			0,
			EXPERIENCE_FIELD_MAX_LENGTH - 5,
		)

		setError('')
		setExperienceToDuplicate(experience)
		setNewExperience({
			name: copyName,
			slug: `${slugBase.replace(/-+$/, '')}-copy`,
		})
		setShowModal(true)
	}

	useEffect(() => {
		if (!orgSlug) {
			setIsLoading(false)
			return
		}

		let cancelled = false

		const loadExperiences = async (isInitialLoad = false) => {
			if (isInitialLoad) {
				setIsLoading(true)
				setError('')
			}
			try {
				const data = await getOrgExperiences(orgSlug)
				if (!cancelled) setExperiences(data)
			} catch (err) {
				console.error('Failed to load org experiences:', err)
				if (!cancelled && isInitialLoad) {
					setError('Failed to load organization experiences.')
				}
			} finally {
				if (!cancelled && isInitialLoad) setIsLoading(false)
			}
		}

		loadExperiences(true)
		const refreshInterval = setInterval(
			loadExperiences,
			STATS_REFRESH_INTERVAL_MS,
		)

		return () => {
			cancelled = true
			clearInterval(refreshInterval)
		}
	}, [orgSlug])

	const handleCreateExperience = async () => {
		const normalizedName = newExperience.name.trim()
		const normalizedSlug = generateSlug(newExperience.slug)

		if (!normalizedName) {
			setError('Experience name cannot be empty.')
			return
		}
		if (!normalizedSlug) {
			setError('Slug cannot be empty.')
			return
		}
		if (normalizedName.length > EXPERIENCE_FIELD_MAX_LENGTH) {
			setError(
				`Experience name must be ${EXPERIENCE_FIELD_MAX_LENGTH} characters or fewer.`,
			)
			return
		}
		if (normalizedSlug.length > EXPERIENCE_FIELD_MAX_LENGTH) {
			setError(
				`Slug must be ${EXPERIENCE_FIELD_MAX_LENGTH} characters or fewer.`,
			)
			return
		}

		setIsSubmitting(true)
		setError('')

		try {
			const payload = {
				orgSlug,
				name: normalizedName,
				slug: normalizedSlug,
			}
			if (experienceToDuplicate) {
				await duplicateExperience({
					...payload,
					experienceSlug: experienceToDuplicate.slug,
				})
			} else {
				await createExperience(payload)
			}

			setShowModal(false)
			setExperienceToDuplicate(null)
			setNewExperience({ name: '', slug: '' })

			const updated = await getOrgExperiences(orgSlug)
			setExperiences(updated)
		} catch (err) {
			console.error('Failed to save experience:', err)
			setError(
				err?.message ||
					(experienceToDuplicate
						? 'Failed to duplicate experience.'
						: 'Failed to create experience.'),
			)
		} finally {
			setIsSubmitting(false)
		}
	}

	const activeExperiences = useMemo(
		() =>
			experiences
				.filter((exp) => exp.status === 'active')
				.sort(sortExperiencesByDate),
		[experiences],
	)
	const inactiveExperiences = useMemo(
		() =>
			experiences
				.filter((exp) => exp.status === 'inactive')
				.sort(sortExperiencesByDateDescending),
		[experiences],
	)

	useEffect(() => {
		setActivePage(1)
	}, [activeExperiences.length])

	useEffect(() => {
		setInactivePage(1)
	}, [searchTerm, inactiveExperiences.length])

	const filteredInactiveExperiences = useMemo(() => {
		const term = searchTerm.trim().toLowerCase()
		if (!term) return inactiveExperiences

		return inactiveExperiences.filter((exp) =>
			[
				exp.name,
				exp.slug,
				exp.status,
				formatExperienceDates(exp),
				getExperienceTiming(exp).label,
			].some((value) =>
				String(value || '')
					.toLowerCase()
					.includes(term),
			),
		)
	}, [inactiveExperiences, searchTerm])

	const activeTotalPages = Math.ceil(
		activeExperiences.length / ACTIVE_PAGE_SIZE,
	)
	const inactiveTotalPages = Math.ceil(
		filteredInactiveExperiences.length / INACTIVE_PAGE_SIZE,
	)
	const paginatedActiveExperiences = activeExperiences.slice(
		(activePage - 1) * ACTIVE_PAGE_SIZE,
		activePage * ACTIVE_PAGE_SIZE,
	)
	const paginatedFilteredInactiveExperiences =
		filteredInactiveExperiences.slice(
			(inactivePage - 1) * INACTIVE_PAGE_SIZE,
			inactivePage * INACTIVE_PAGE_SIZE,
		)
	const hasSearch = searchTerm.trim().length > 0
	const navigateToExperienceSearch = (experienceSlug) => {
		navigate(`/${orgSlug}/experiences/${experienceSlug}`)
	}
	const handleCardClick = (event, experienceSlug) => {
		if (event.target.closest?.(CARD_INTERACTIVE_TARGET_SELECTOR)) return
		navigateToExperienceSearch(experienceSlug)
	}
	const handleCardKeyDown = (event, experienceSlug) => {
		if (event.target !== event.currentTarget) return
		if (event.key !== 'Enter' && event.key !== ' ') return

		event.preventDefault()
		navigateToExperienceSearch(experienceSlug)
	}
	const renderExperienceMenu = (experience) => (
		<Menu position='bottom-end' withinPortal shadow='md'>
			<Menu.Target>
				<ActionIcon
					variant='subtle'
					aria-label={`More actions for ${experience.name}`}
				>
					<DotsVertical size={18} />
				</ActionIcon>
			</Menu.Target>
			<Menu.Dropdown>
				<Menu.Item
					icon={<Copy size={16} />}
					onClick={() => openDuplicateModal(experience)}
				>
					Duplicate
				</Menu.Item>
			</Menu.Dropdown>
		</Menu>
	)

	if (isLoading) {
		return (
			<Box px='md' py='md'>
				<Title order={2} mb='md'>
					Organization Experiences
				</Title>
				<Group position='center' py='xl'>
					<Loader aria-label='Loading experiences' />
				</Group>
			</Box>
		)
	}

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='md'>
				Organization Experiences
			</Title>

			{error && (
				<Text color='red' mb='md'>
					{error}
				</Text>
			)}

			{activeExperiences.length > 0 ? (
				<>
					<Title order={3} mt='xl' mb='md'>
						Active Experiences
					</Title>
					<SimpleGrid
						cols={3}
						spacing='lg'
						mb='xl'
						breakpoints={[
							{ maxWidth: 'lg', cols: 2, spacing: 'md' },
							{ maxWidth: 'md', cols: 1, spacing: 'sm' },
						]}
					>
						{paginatedActiveExperiences.map((exp) => {
							const timing = getExperienceTiming(exp)
							const isLive = timing.key === 'live'

							return (
								<Card
									key={exp.id}
									shadow='sm'
									p='lg'
									radius='md'
									withBorder
									role='link'
									tabIndex={0}
									aria-label={`Open ${exp.name} check-in search`}
									onClick={(event) => handleCardClick(event, exp.slug)}
									onKeyDown={(event) =>
										handleCardKeyDown(event, exp.slug)
									}
									style={{
										cursor: 'pointer',
										...(isLive
											? {
												borderColor: '#DC1817',
												boxShadow:
													'0 0 0 1px rgba(220, 24, 23, 0.18), 0 12px 28px rgba(220, 24, 23, 0.16)',
												background:
													'linear-gradient(135deg, rgba(220, 24, 23, 0.09), rgba(220, 24, 23, 0.03) 55%, transparent)',
											}
											: {}),
									}}
								>
									<Stack spacing='xs'>
										<Group position='apart' align='flex-start' noWrap>
											<Box>
												<Group spacing='xs' align='center'>
													<Title order={4}>{exp.name}</Title>
													<Badge
														color={timing.color}
														variant={isLive ? 'filled' : 'light'}
														style={
															isLive
																? { backgroundColor: '#DC1817' }
																: undefined
														}
													>
														{timing.label}
													</Badge>
												</Group>
												<Text size='sm' color='dimmed' mt={4}>
													{formatExperienceDates(exp)}
												</Text>
											</Box>
											{renderExperienceMenu(exp)}
										</Group>

										{exp.stats ? (
											<>
												<Box pos='relative'>
													<Progress
														value={exp.stats.progress}
														size={20}
														radius='xl'
													/>
													<Text
														size='xs'
														fw={600}
														pos='absolute'
														left='50%'
														top='50%'
														style={{
															transform: 'translate(-50%, -50%)',
															pointerEvents: 'none',
														}}
													>
														{`${exp.stats.totalCompletedCheckIns} / ${exp.stats.totalCheckIns} • ${exp.stats.progress}%`}
													</Text>
												</Box>
												<Text size='sm' color='dimmed'>
													Staff: {exp.stats.staffCount}
												</Text>
											</>
										) : (
											<Progress
												value={0}
												label='No data'
												size={20}
												radius='xl'
											/>
										)}

										<Group mt='sm' spacing='xs' position='left'>
											<Button
												size='xs'
												onClick={() =>
													navigate(
														`/${orgSlug}/experiences/${exp.slug}/overview`,
													)
												}
											>
												Overview
											</Button>
											<Button
												size='xs'
												onClick={() =>
													navigate(
														`/${orgSlug}/experiences/${exp.slug}/manage/checkins`,
													)
												}
											>
												Check-Ins
											</Button>
											<Button
												size='xs'
												onClick={() =>
													navigate(
														`/${orgSlug}/experiences/${exp.slug}/manage/staff`,
													)
												}
											>
												Staff
											</Button>
											<Button
												size='xs'
												variant='light'
												aria-label={`Edit ${exp.name} settings`}
												onClick={() =>
													navigate(
														`/${orgSlug}/experiences/${exp.slug}/manage/details`,
													)
												}
											>
												<Settings size={16} />
											</Button>
										</Group>
									</Stack>
								</Card>
							)
						})}
					</SimpleGrid>
					{activeTotalPages > 1 && (
						<Group position='center' mb='xl'>
							<Pagination
								value={activePage}
								onChange={setActivePage}
								total={activeTotalPages}
								size='sm'
							/>
						</Group>
					)}
				</>
			) : (
				<Text>No active experiences.</Text>
			)}

			{/* Inactives Section */}
			<Title order={3} mt='xl' mb='md'>
				Inactive Experiences
			</Title>

			<Button mb='lg' onClick={openCreateModal}>
				➕ Create New Experience
			</Button>

			<Group mb='md' spacing='sm' position='apart' align='flex-end'>
				<TextInput
					placeholder='Search inactive experiences...'
					value={searchTerm}
					onChange={(event) => setSearchTerm(event.target.value)}
					style={{ flex: 1, minWidth: 240 }}
				/>
			</Group>

			{filteredInactiveExperiences.length > 0 ? (
				<>
					<Table withBorder striped highlightOnHover>
						<thead>
							<tr>
								<th>Name</th>
								<th>Dates</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{paginatedFilteredInactiveExperiences.map((exp) => {
								return (
									<tr key={exp.id}>
										<td>{exp.name}</td>
										<td>{formatExperienceDates(exp)}</td>
										<td style={{ textAlign: 'right' }}>
											<Group spacing='xs' position='right'>
												<Button
													size='xs'
													onClick={() =>
														navigate(
															`/${orgSlug}/experiences/${exp.slug}/manage/checkins`,
														)
													}
												>
													Check-Ins
												</Button>
												<Button
													size='xs'
													onClick={() =>
														navigate(
															`/${orgSlug}/experiences/${exp.slug}/manage/staff`,
														)
													}
												>
													Staff
												</Button>
												<Button
													size='xs'
													variant='white'
													aria-label={`Edit ${exp.name} settings`}
													onClick={() =>
														navigate(
															`/${orgSlug}/experiences/${exp.slug}/manage/details`,
														)
													}
												>
													<Settings size={16} />
												</Button>
												{renderExperienceMenu(exp)}
											</Group>
										</td>
									</tr>
								)
							})}
						</tbody>
					</Table>
					{inactiveTotalPages > 1 && (
						<Group position='center' mt='md'>
							<Pagination
								value={inactivePage}
								onChange={setInactivePage}
								total={inactiveTotalPages}
								size='sm'
							/>
						</Group>
					)}
				</>
			) : (
				<Text>
					{hasSearch
						? 'No inactive experiences match your search.'
						: 'No inactive experiences.'}
				</Text>
			)}

			{/* Modal */}
			<Modal
				opened={showModal}
				onClose={closeExperienceModal}
				title={
					experienceToDuplicate
						? `Duplicate ${experienceToDuplicate.name}`
						: 'Create New Experience'
				}
				centered
				closeOnClickOutside={!isSubmitting}
				closeOnEscape={!isSubmitting}
			>
				{error && (
					<Text color='red' mb='sm'>
						{error}
					</Text>
				)}
				<form
					onSubmit={(e) => {
						e.preventDefault()
						handleCreateExperience()
					}}
				>
					<TextInput
						label='Experience Name'
						value={newExperience.name}
						onChange={(e) => {
							const name = e.target.value
								.replace(/^\s+/, '')
								.slice(0, EXPERIENCE_FIELD_MAX_LENGTH)
							setNewExperience({
								name,
								slug: generateSlug(name),
							})
						}}
						onBlur={(e) => {
							const name = e.target.value.trim()
							setNewExperience({
								name,
								slug: generateSlug(name),
							})
						}}
						maxLength={EXPERIENCE_FIELD_MAX_LENGTH}
						required
						disabled={isSubmitting}
						mb='sm'
					/>

					<TextInput
						label='Slug (cannot be changed later)'
						value={newExperience.slug}
						onChange={(e) =>
							setNewExperience({
								...newExperience,
								slug: generateSlug(e.target.value).slice(
									0,
									EXPERIENCE_FIELD_MAX_LENGTH,
								),
							})
						}
						maxLength={EXPERIENCE_FIELD_MAX_LENGTH}
						required
						disabled={isSubmitting}
					/>

					<Group mt='lg' position='right'>
						<Button
							variant='light'
							onClick={closeExperienceModal}
							disabled={isSubmitting}
						>
							Cancel
						</Button>
						<Button type='submit' loading={isSubmitting}>
							{experienceToDuplicate ? 'Create Copy' : 'Create'}
						</Button>
					</Group>
				</form>
			</Modal>
		</Box>
	)
}

export default ManageExperiencesPage
